import {defineStore} from "pinia";
import Host from "../core/interfaces/Host";
import LegacyAccess from "@/core/interfaces/Access";
import StorageService from "@/core/services/StorageService";
import {apodisCoinApi, environmentApi, hostApi, notificationApi} from "@/container";
import {ApodisCoinPurchasable} from "@/core/interfaces/ApodisCoin";
import useCpsLocator from "@/amelipro/composables/useCpsLocator";
import {supportsLiveServiceUpdate, supportsMachineIdentity} from "@/core/services/shellCapabilities";
import spreadStartupRequest from "@/core/http/spreadStartupRequest";

/**
 * The environment read in flight, shared by every caller.
 *
 * The boot of a window and a refresh asked by a screen can land at the same
 * moment; one read answers both instead of two requests racing.
 */
let pendingEnvironmentRead: Promise<LegacyAccess> | null = null;

export const useSystemStore = defineStore('system', {
    state: () => {
        return {
            host: null as Host | null,
            environment: null as LegacyAccess | null,
            /**
             * When the environment was last read successfully.
             *
             * Held here rather than in the screens that refresh it: the boot of the
             * window loads it too, and a budget only one caller knows about would
             * make that screen ask again straight away.
             */
            environmentLoadedAt: null as number | null,
            notificationCount: 0 as number,
            groupingLogo: undefined as string | undefined,
            /** The grouping of the connected pharmacy, set when it is an Apodis partner. */
            groupingId: undefined as number | undefined,
            groupingName: undefined as string | undefined,
            /** The branding background the grouping logo is meant to sit on. */
            groupingLogoBackground: undefined as string | undefined,
            coinBalance: 0 as number,
            /** Whether the balance was read, so a 0 is not shown while it loads in the background. */
            coinBalanceLoaded: false as boolean,
            coinStoreItems: [] as ApodisCoinPurchasable[],
            unsubscribeEvent: undefined as any
        };
    },
    getters: {
        /**
         * Know if the current user is an admin.
         */
        isAdmin(): boolean {
            return !!this.environment?.user?.is_superadmin;
        }
    },
    actions: {
        /**
         * Init system store.
         *
         * Only what Connect needs to start is awaited. The notification count and
         * the Apodis Coins only feed badges: they are loaded in the background,
         * spread over the next minute, so the Connect started at the opening hour
         * do not all ask for them at once. They are scheduled once the start has
         * succeeded, as a failed init is run again.
         */
        async init() {
            return Promise.all([
                this.updateHostName(),
                this.loadEnvironment()
            ])
                .then(() => {
                    spreadStartupRequest(() => this.loadNotificationCount());
                    spreadStartupRequest(() => this.loadApodisCoins());
                    spreadStartupRequest(() => this.loadApodisCoinStore());

                    this.unsubscribeEvent = window.ipcRenderer.socketListenEvent('apodis-coin.change', (event, {data}) => {
                        this.coinBalance = data.data.balance;
                        this.coinBalanceLoaded = true;
                    });
                    StorageService().store('systemLoaded', 'true');
                });
        },
        /**
         * Refresh store if necessary
         */
        async refresh() {
            const systemLoaded = await StorageService().read('systemLoaded');
            const systemIsLoaded = systemLoaded === 'true';

            if (systemIsLoaded && this.environment === null && this.host === null) {
                return this.init();
            }

        },
        /**
         * Update host name, with the workstation identity and the Apodis Live
         * version when the shell provides them.
         */
        async updateHostName() {
            const version = await this.getVersion();
const getMachineIdentity = window.ipcRenderer.getMachineIdentity;
            const identity = supportsMachineIdentity() && getMachineIdentity
                ? await getMachineIdentity().catch(() => ({}))
                : {};
            // Null once the service is gone. Left out by a shell that cannot
            // tell, so the version recorded for the host stays.
            const liveVersion = supportsLiveServiceUpdate()
                ? (await window.ipcRenderer.getLivePatientWatchStatus?.().catch(() => null))?.serviceVersion
                : undefined;

            return this.getHostname()
                .then((hostName: string) => {
                    hostApi()
                        .update({
                            name: hostName,
                            connect_version: version,
                            ...identity,
                            ...(liveVersion !== undefined ? {live_version: liveVersion} : {}),
                        })
                        .then(host => {
                            this.host = host;
                        });
                });
        },
        /**
         * Load the notification count.
         */
        async loadNotificationCount(): Promise<void> {
            return notificationApi()
                .count()
                .then(response => {
                    this.notificationCount = response.count;
                });
        },
        /**
         * Read the environment again and update everything that derives from it.
         */
        async refreshEnvironment(): Promise<LegacyAccess> {
            if (pendingEnvironmentRead) {
                return pendingEnvironmentRead;
            }

            pendingEnvironmentRead = environmentApi().read()
                .then(environment => {
                    if (!environment) {
                        throw new Error('Unable to load the environment.');
                    }

                    this.environment = environment;
                    this.environmentLoadedAt = Date.now();

                    if (environment.entity?.grouping?.is_partner) {
                        this.groupingLogo = environment.entity.grouping.branding?.logo?.renderPath;
                        this.groupingId = environment.entity.grouping.id;
                        this.groupingName = environment.entity.grouping.name;

                        const branding = environment.entity.grouping.branding;

                        this.groupingLogoBackground = branding?.topBarBackGround?.trim()
                            || branding?.leftBarBackGround?.trim()
                            || undefined;
                    }
                    if (environment.entity?.lgo) {
                        StorageService().store('lgoId', String(environment.entity?.lgo));
                    }

                    return environment;
                })
                .finally(() => {
                    pendingEnvironmentRead = null;
                });

            return pendingEnvironmentRead;
        },
        /**
         * Load the environment and join the channels that depend on it.
         */
        async loadEnvironment(): Promise<void> {
            const environment = await this.refreshEnvironment();

            this.initializeOrganizationSocket(environment.organizationId);
            this.subscribeCpsLocator(environment.organizationId);
        },
        async loadApodisCoins(): Promise<void> {
            return apodisCoinApi().balance()
                .then(response => {
                    this.coinBalance = response.balance;
                    this.coinBalanceLoaded = true;
                });
        },
        async loadApodisCoinStore(): Promise<void> {
            return apodisCoinApi().store()
                .then(response => {
                    this.coinStoreItems = response;
                });
        },
        /**
         * Reload the apodis coin balance after a stripe checkout completed.
         */
        async reloadApodisCoinsAfterCheckout(attempts = 6, delayMs = 1500): Promise<void> {
            const initialBalance = this.coinBalance;

            for (let attempt = 0; attempt < attempts; attempt++) {
                await this.loadApodisCoins();

                if (this.coinBalance !== initialBalance) {
                    break;
                }

                await new Promise(resolve => setTimeout(resolve, delayMs));
            }

            await this.loadApodisCoinStore();
        },
        /**
         * Retrieve the host name from electron process.
         */
        async getHostname(): Promise<string> {
            return await window.ipcRenderer.invoke('getHostname');
        },
        /** Retrieve the version from the electron process. */
        async getVersion(): Promise<string> {
            return await window.ipcRenderer.invoke('electron-version');
        },
        /**
         * Subscribe to the organization socket channel.
         *
         * @param organizationId
         */
        initializeOrganizationSocket(organizationId: number) {
            window.ipcRenderer.socketSubscribe(`private-organization.${organizationId}`);
        },
        /**
         * Keep this Connect's map of the pharmacy's CPS cards up to date.
         *
         * Every window listens, so any screen can say where the physical card
         * is; the notification window alone answers the requests, since it is
         * the one that owns the reader.
         *
         * @param organizationId The organization of the pharmacy.
         */
        async subscribeCpsLocator(organizationId: number) {
            const hostname = await this.getHostname().catch(() => null);

            useCpsLocator().subscribe(organizationId, hostname);
        },
    }
});
